/**
 * Auth API - Cognito Integration (USER_PASSWORD_AUTH)
 * Uses direct Cognito API calls instead of SRP to avoid
 * browser crypto issues with amazon-cognito-identity-js
 */

import { COGNITO_CONFIG, ensureSessionEnvironment } from './config';

const COGNITO_ENDPOINT = `https://cognito-idp.${COGNITO_CONFIG.region}.amazonaws.com/`;

export interface AuthResult {
  success: boolean;
  token?: string;
  email?: string;
  error?: string;
}

export interface SessionResult {
  valid: boolean;
  email?: string;
  token?: string;
}

function decodeJwtPayload(token: string): Record<string, unknown> {
  const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
  return JSON.parse(atob(base64));
}

async function cognitoRequest(action: string, body: Record<string, unknown>): Promise<Response> {
  return fetch(COGNITO_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': `AWSCognitoIdentityProviderService.${action}`,
    },
    body: JSON.stringify(body),
  });
}

export const authApi = {
  /**
   * Sign up with email and password
   */
  signUp: async (email: string, password: string): Promise<AuthResult> => {
    try {
      const response = await cognitoRequest('SignUp', {
        ClientId: COGNITO_CONFIG.clientId,
        Username: email,
        Password: password,
        UserAttributes: [
          { Name: 'email', Value: email },
        ],
      });

      const data = await response.json();

      if (!response.ok) {
        const errorMessage = data.message || data.__type || 'Sign up failed';
        return { success: false, error: errorMessage };
      }

      return { success: true, email };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error',
      };
    }
  },

  /**
   * Confirm sign up with verification code
   */
  confirmSignUp: async (email: string, code: string): Promise<AuthResult> => {
    try {
      const response = await cognitoRequest('ConfirmSignUp', {
        ClientId: COGNITO_CONFIG.clientId,
        Username: email,
        ConfirmationCode: code,
      });

      const data = await response.json();

      if (!response.ok) {
        const errorMessage = data.message || data.__type || 'Confirmation failed';
        return { success: false, error: errorMessage };
      }

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error',
      };
    }
  },

  /**
   * Resend confirmation code
   */
  resendCode: async (email: string): Promise<AuthResult> => {
    try {
      const response = await cognitoRequest('ResendConfirmationCode', {
        ClientId: COGNITO_CONFIG.clientId,
        Username: email,
      });

      const data = await response.json();

      if (!response.ok) {
        const errorMessage = data.message || data.__type || 'Failed to resend code';
        return { success: false, error: errorMessage };
      }

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error',
      };
    }
  },

  /**
   * Sign in with email and password (USER_PASSWORD_AUTH flow)
   */
  signIn: async (email: string, password: string): Promise<AuthResult> => {
    try {
      const response = await cognitoRequest('InitiateAuth', {
        AuthFlow: 'USER_PASSWORD_AUTH',
        ClientId: COGNITO_CONFIG.clientId,
        AuthParameters: {
          USERNAME: email,
          PASSWORD: password,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        const errorMessage = data.message || data.__type || 'Authentication failed';
        console.error('[Auth] Sign in failed:', errorMessage);
        return { success: false, error: errorMessage };
      }

      if (data.ChallengeName === 'NEW_PASSWORD_REQUIRED') {
        return { success: false, error: 'NEW_PASSWORD_REQUIRED' };
      }

      const { IdToken, AccessToken, RefreshToken } = data.AuthenticationResult;

      // A completed password sign-in replaces any older in-flight OAuth attempt.
      sessionStorage.removeItem('mindmeld_oauth_pending');
      sessionStorage.removeItem('mindmeld_oauth_exchange');
      // Store tokens for session management
      localStorage.removeItem('mindmeld_auth_method');
      localStorage.setItem('mindmeld_token', IdToken);
      localStorage.setItem('mindmeld_access_token', AccessToken);
      if (RefreshToken) {
        localStorage.setItem('mindmeld_refresh_token', RefreshToken);
      }

      const payload = decodeJwtPayload(IdToken);

      return {
        success: true,
        token: IdToken,
        email: (payload.email as string) || email,
      };
    } catch (error) {
      console.error('[Auth] Sign in error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error',
      };
    }
  },

  /**
   * Forgot password — sends verification code to email
   */
  forgotPassword: async (email: string): Promise<AuthResult> => {
    try {
      const response = await cognitoRequest('ForgotPassword', {
        ClientId: COGNITO_CONFIG.clientId,
        Username: email,
      });

      const data = await response.json();

      if (!response.ok) {
        const errorMessage = data.message || data.__type || 'Failed to send reset code';
        return { success: false, error: errorMessage };
      }

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error',
      };
    }
  },

  /**
   * Confirm forgot password — sets new password with verification code
   */
  confirmForgotPassword: async (email: string, code: string, newPassword: string): Promise<AuthResult> => {
    try {
      const response = await cognitoRequest('ConfirmForgotPassword', {
        ClientId: COGNITO_CONFIG.clientId,
        Username: email,
        ConfirmationCode: code,
        Password: newPassword,
      });

      const data = await response.json();

      if (!response.ok) {
        const errorMessage = data.message || data.__type || 'Failed to reset password';
        return { success: false, error: errorMessage };
      }

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error',
      };
    }
  },

  /**
   * Sign out — clear all stored tokens
   */
  signOut: async (): Promise<void> => {
    sessionStorage.removeItem('mindmeld_oauth_pending');
    sessionStorage.removeItem('mindmeld_oauth_exchange');
    localStorage.removeItem('mindmeld_auth_method');
    localStorage.removeItem('mindmeld_token');
    localStorage.removeItem('mindmeld_access_token');
    localStorage.removeItem('mindmeld_refresh_token');
    localStorage.removeItem('mindmeld_email');

    // Clean up any legacy amazon-cognito-identity-js tokens
    const keys = Object.keys(localStorage);
    for (const key of keys) {
      if (key.startsWith('CognitoIdentityServiceProvider.')) {
        localStorage.removeItem(key);
      }
    }
  },

  /**
   * Get current session — validates stored token, refreshes if expired
   */
  getCurrentSession: async (): Promise<SessionResult> => {
    if (!ensureSessionEnvironment()) return { valid: false };
    const idToken = localStorage.getItem('mindmeld_token');
    const refreshToken = localStorage.getItem('mindmeld_refresh_token');

    if (!idToken) {
      return { valid: false };
    }

    try {
      const payload = decodeJwtPayload(idToken);
      const now = Math.floor(Date.now() / 1000);

      // Token still valid (with 60s buffer)
      if ((payload.exp as number) > now + 60) {
        return {
          valid: true,
          email: payload.email as string,
          token: idToken,
        };
      }

      // Token expired — try refresh
      if (!refreshToken) {
        return { valid: false };
      }

      if (localStorage.getItem('mindmeld_auth_method') === 'oauth') {
        const { refreshOAuthSession } = await import('./oauth');
        return refreshOAuthSession();
      }
      const response = await cognitoRequest('InitiateAuth', {
        AuthFlow: 'REFRESH_TOKEN_AUTH',
        ClientId: COGNITO_CONFIG.clientId,
        AuthParameters: {
          REFRESH_TOKEN: refreshToken,
        },
      });

      if (!response.ok) {
        // Refresh failed — clear stored tokens
        localStorage.removeItem('mindmeld_token');
        localStorage.removeItem('mindmeld_access_token');
        localStorage.removeItem('mindmeld_refresh_token');
        return { valid: false };
      }

      const data = await response.json();
      const newIdToken = data.AuthenticationResult.IdToken;
      const newAccessToken = data.AuthenticationResult.AccessToken;

      localStorage.setItem('mindmeld_token', newIdToken);
      localStorage.setItem('mindmeld_access_token', newAccessToken);

      const newPayload = decodeJwtPayload(newIdToken);
      return {
        valid: true,
        email: newPayload.email as string,
        token: newIdToken,
      };
    } catch {
      return { valid: false };
    }
  },

  /**
   * Get current user email from stored token
   */
  getCurrentUser: () => {
    if (!ensureSessionEnvironment()) return null;
    const token = localStorage.getItem('mindmeld_token');
    if (!token) return null;
    try {
      const payload = decodeJwtPayload(token);
      return { getUsername: () => payload.email as string };
    } catch {
      return null;
    }
  },
};
